import { memo, useCallback, useState } from "react";
import { clsx } from "clsx";

import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { useAppSelector } from "@/shared/lib/hooks/redux";
import { ActionBtn } from "@/shared/ui/Btn";
import { MostPopular } from "@/shared/ui/MostPopular";

import WhiteCheckIcon from "@/shared/assets/icons/whiteCheck.svg";

import { ProductInterval } from "../../const/subscription";
import { calcAnnualSavings } from "../../lib/calcAnnualSavings";
import { getBaseProductName } from "../../lib/getBaseProductName";
import { getProductsList } from "../../model/selectors/subscriptionSelectors";
import type { ProductData } from "../../model/types/productData";

import cls from "./BillingSelection.module.scss";

type BillingSelectionProps = {
  className?: string;
  optionsClassName?: string;
  variant?: "dark" | "light";
  title?: string;
  description?: string;
  submitText?: string;
  backText?: string;
  isBusy?: boolean;
  showSelectedProduct?: boolean;
  /** Tier picked in the plan step, either its monthly or its annual price. */
  stripePriceId?: string | null;
  onSubmit: (product: ProductData) => void;
  onBack?: () => void;
};

export const BillingSelection = memo((props: BillingSelectionProps) => {
  const {
    className,
    optionsClassName,
    variant = "dark",
    title,
    description,
    submitText = "CONTINUE",
    backText = "BACK",
    isBusy,
    showSelectedProduct = true,
    stripePriceId,
    onSubmit,
    onBack,
  } = props;

  const { showAlert } = useAlert();
  const [selectedInterval, setSelectedInterval] = useState(ProductInterval.Yearly);

  const productsList = useAppSelector(getProductsList);

  const selectedProduct = productsList.find((productData) => productData.stripePriceId === stripePriceId);

  const productName = getBaseProductName(selectedProduct?.name);
  const monthlyProduct = productsList.find((productData) => productData.name === productName);
  const yearlyProduct = productsList.find((productData) => productData.name === `${productName} Annual`);
  const selectedIntervalProduct = selectedInterval === ProductInterval.Monthly ? monthlyProduct : yearlyProduct;

  const onChange = useCallback((interval: ProductInterval) => {
    setSelectedInterval(interval);
  }, []);

  const onClickSubmit = useCallback(() => {
    if (!selectedIntervalProduct) {
      showAlert({ theme: AlertTheme.ERROR, message: "Choose a billing interval" } as AlertOptions);
      return;
    }

    onSubmit(selectedIntervalProduct);
  }, [onSubmit, selectedIntervalProduct, showAlert]);

  const renderItem = (productData: ProductData) => {
    const isSelected = selectedInterval === productData.interval;

    return (
      <div
        key={productData.stripePriceId}
        className={clsx(cls.Product, isSelected && cls.isSelected)}
        onClick={() => onChange(productData.interval)}
      >
        {productData.interval === ProductInterval.Yearly && (
          <MostPopular position="left" variant="dark" text="BEST VALUE" />
        )}
        <div className={cls.LeftSide}>
          <div className={cls.Name}>{productData.name ?? "None"}</div>
          <div className={cls.Info}>
            {productData.interval === ProductInterval.Monthly && <p>Cancel any time</p>}
            {productData.interval === ProductInterval.Yearly && monthlyProduct && (
              <p>
                <b>Save ${calcAnnualSavings(productData, monthlyProduct).toFixed(0)}</b> (2 months <b>FREE</b>)
              </p>
            )}
          </div>
        </div>
        <div className={cls.RightSide}>
          <div className={cls.Price}>${Number(productData.price).toFixed(0)}</div>
          <div className={cls.Interval}>/{productData.interval === ProductInterval.Monthly ? "month" : "year"}</div>
        </div>
        {isSelected && <WhiteCheckIcon className={cls.CheckIcon} />}
      </div>
    );
  };

  return (
    <div className={clsx(cls.Container, cls[variant], className)}>
      {showSelectedProduct && monthlyProduct && (
        <div className={clsx(cls.Product, cls.SelectedProduct)}>
          <div className={cls.LeftSide}>
            <div className={cls.Name}>{monthlyProduct.name ?? "None"}</div>
            <div className={cls.Info}>
              <p>Unlimited readings</p>
              <p>
                {monthlyProduct.maxPersonalityChanges === 999 ? "Unlimited" : monthlyProduct.maxPersonalityChanges}{" "}
                {monthlyProduct.maxPersonalityChanges === 1 ? "psychic" : "psychics"}
              </p>
            </div>
          </div>
          <div className={cls.RightSide}>
            <div className={cls.Price}>${Number(monthlyProduct.price).toFixed(0)}</div>
            <div className={cls.Interval}>/month</div>
          </div>
        </div>
      )}
      {title && <div className={cls.Title}>{title}</div>}
      {description && <div className={cls.Description}>{description}</div>}
      <div className={clsx(cls.Options, optionsClassName)}>
        {monthlyProduct && renderItem(monthlyProduct)}
        {yearlyProduct && renderItem(yearlyProduct)}
      </div>
      <div className={cls.ButtonContainer}>
        {onBack && (
          <ActionBtn variant="navy" onClick={onBack}>
            {backText}
          </ActionBtn>
        )}
        <ActionBtn disabled={!selectedIntervalProduct} isBusy={isBusy} onClick={onClickSubmit}>
          {submitText}
        </ActionBtn>
      </div>
    </div>
  );
});
